import { useCallback, useEffect, useMemo, useState } from "react";
import { AdminButton, AdminErrorBanner, AdminPageLayout } from "@/components/admin";
import { fetchAdminLocations } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import type { AdminLocationItem } from "@/types.ts";
import SummaryCards from "./components/SummaryCards";
import LocationTable from "./components/LocationTable";
import QuickFilters from "./components/QuickFilters";
import TodaySummaryCard from "./components/TodaySummaryCard";
import InfoCard from "./components/InfoCard";

export type AdminLocationRecord = AdminLocationItem & {
  id: string;
  recentLocation: string;
  collectionMethod: string;
  accuracy: string;
  receivedAt: string;
  riskLevel: "normal" | "warning" | "stale" | "empty";
};

export type LocationSummary = {
  totalCount: number;
  normalCount: number;
  checkNeededCount: number;
  staleCount: number;
};

const getRiskLevel = (
  item: AdminLocationItem,
  normalIds: Set<number>,
  staleIds: Set<number>,
  warningIds: Set<number>,
): AdminLocationRecord["riskLevel"] => {
  if (!item.locationLogId) return "empty";
  if (staleIds.has(item.seniorUserAccountId)) return "stale";
  if (warningIds.has(item.seniorUserAccountId)) return "warning";
  if (normalIds.has(item.seniorUserAccountId)) return "normal";
  return "warning";
};

const getLocationLabel = (item: AdminLocationItem) => {
  const address = item.addressDisplayLabel || item.addressText;
  if (address) return address;
  const area = [item.sido, item.sigungu].filter(Boolean).join(" ");
  if (area) return area;
  if (item.latitude != null && item.longitude != null) {
    return `${Number(item.latitude).toFixed(6)}, ${Number(item.longitude).toFixed(6)}`;
  }
  return "위치 미수신";
};

const getAccuracyLabel = (accuracyMeter: number | null) => {
  if (accuracyMeter == null) return "-";
  return `${Math.round(Number(accuracyMeter))}m`;
};

const toLocationRecord = (
  item: AdminLocationItem,
  normalIds: Set<number>,
  staleIds: Set<number>,
  warningIds: Set<number>,
): AdminLocationRecord => ({
  ...item,
  id: `${item.seniorUserAccountId}-${item.locationLogId ?? "none"}`,
  recentLocation: getLocationLabel(item),
  collectionMethod: item.source || "미확인",
  accuracy: getAccuracyLabel(item.accuracyMeter),
  receivedAt: formatDateTime(item.collectedAt),
  riskLevel: getRiskLevel(item, normalIds, staleIds, warningIds),
});

const applyFilter = (rows: AdminLocationRecord[], activeFilter: string, searchQuery: string) => {
  const query = searchQuery.trim().toLowerCase();
  return rows.filter((row) => {
    if (activeFilter === "normal" && row.riskLevel !== "normal") return false;
    if (activeFilter === "warning" && row.riskLevel !== "warning") return false;
    if (activeFilter === "stale" && row.riskLevel !== "stale") return false;
    if (activeFilter === "empty" && row.riskLevel !== "empty") return false;
    if (activeFilter === "permission" && row.permissionStatus === "GRANTED") return false;
    if (activeFilter === "provider" && row.providerStatus === "ENABLED") return false;
    if (!query) return true;
    return [
      row.elderlyName,
      row.deviceName,
      row.recentLocation,
      row.addressText,
      row.addressDisplayLabel,
      String(row.seniorUserAccountId),
      row.deviceId == null ? "" : String(row.deviceId),
    ].some((value) => value?.toLowerCase().includes(query));
  });
};

const LocationPage = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [records, setRecords] = useState<AdminLocationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const [items, normalItems, staleItems, permissionItems, providerItems] = await Promise.all([
        fetchAdminLocations(),
        fetchAdminLocations({ status: "normal" }),
        fetchAdminLocations({ status: "stale" }),
        fetchAdminLocations({ status: "permission" }),
        fetchAdminLocations({ status: "provider" }),
      ]);
      const normalIds = new Set(normalItems.map((item) => item.seniorUserAccountId));
      const staleIds = new Set(staleItems.map((item) => item.seniorUserAccountId));
      const warningIds = new Set([...permissionItems, ...providerItems].map((item) => item.seniorUserAccountId));
      setRecords(items.map((item) => toLocationRecord(item, normalIds, staleIds, warningIds)));
    } catch (error) {
      setRecords([]);
      setErrorMessage(error instanceof Error ? error.message : "위치 정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const filteredRecords = useMemo(
    () => applyFilter(records, activeFilter, searchQuery),
    [activeFilter, records, searchQuery],
  );

  const summary = useMemo<LocationSummary>(() => ({
    totalCount: records.length,
    normalCount: records.filter((row) => row.riskLevel === "normal").length,
    checkNeededCount: records.filter((row) => row.riskLevel === "warning").length,
    staleCount: records.filter((row) => row.riskLevel === "stale" || row.riskLevel === "empty").length,
  }), [records]);

  return (
    <AdminPageLayout
      title="위치 관리"
      subtitle="어르신 최신 위치, 수신 상태, 위치 이력 관리"
      actions={(
        <AdminButton variant="primary" size="sm" icon={loading ? "ri-loader-4-line animate-spin" : "ri-refresh-line"} onClick={loadRecords} disabled={loading}>
          새로고침
        </AdminButton>
      )}
    >
          <div className="mb-4">
            <AdminErrorBanner message={errorMessage} />
          </div>

          <div className="mb-4">
            <SummaryCards summary={summary} />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
            <div className="xl:col-span-3">
              <LocationTable
                records={filteredRecords}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                totalCount={filteredRecords.length}
                loading={loading}
              />
            </div>

            <div className="xl:col-span-1 flex flex-col gap-4">
              <TodaySummaryCard summary={summary} />
              <QuickFilters activeFilter={activeFilter} onFilterChange={setActiveFilter} />
              <InfoCard />
            </div>
          </div>
    </AdminPageLayout>
  );
};

export default LocationPage;
